/**
 * Keeper Duel — self-contained arcade penalty duel runtime hosted in a react-native-webview.
 *
 * The loop: the keeper NEVER stops moving — he patrols the goal mouth and gets faster with every
 * goal; you flick into the space he's leaving, under a shrinking shot clock (hesitate and he steals
 * it). A new hazard unlocks every 3 goals: a defender sprinting across the pitch (blocks the shot),
 * wind that bends the ball (an arrow shows direction/strength — judging the bend is on you), rain that makes the
 * crosshair drift, then a second defender. Outcomes are DETERMINISTIC — goal ⇔ the (wind-adjusted)
 * landing point is inside the frame, outside the keeper's committed reach, and unblocked. The only
 * randomness is hazard placement (wind strength, defender lanes), which the player sees before
 * shooting. First failed kick ends the run; score = consecutive goals.
 *
 * Why a string module: the whole game (canvas render loop + input + hazards + synthesized audio)
 * ships with the app as one HTML document with zero external dependencies — offline from first load.
 *
 * Bridge protocol (window.ReactNativeWebView.postMessage / window.parent.postMessage, JSON):
 *   web → RN:  { type:'ready' } | { type:'goal', streak } | { type:'duel-over', runId, streak, nearMiss }
 *   RN → web:  window.__keeperDuelApplyStart({ bestStreak })
 *
 * Coordinate space is a fixed 720×1080 portrait world, CSS-scaled to fit the WebView (letterboxed).
 *
 * Copy guard: no "bet" (pick/prediction wording only), no official tournament naming.
 */

export function buildKeeperDuelGameHtml(): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
<style>
  * { margin:0; padding:0; box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
  html,body { width:100%; height:100%; overflow:hidden; background:#0B1220; touch-action:none;
    font-family:-apple-system,system-ui,sans-serif; user-select:none; -webkit-user-select:none; }
  #stage { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; }
  canvas { display:block; max-width:100%; max-height:100%; }
</style>
</head>
<body>
<div id="stage"><canvas id="c" width="720" height="1080"></canvas></div>
<script>
(function(){
  'use strict';
  // ---------- World ----------
  var W = 720, H = 1080;
  var GOAL_L = 110, GOAL_R = 610, BAR_Y = 140, LINE_Y = 340;
  var GOAL_CX = (GOAL_L + GOAL_R) / 2;
  var BALL_X = 360, BALL_Y = 870, BALL_R = 24;
  var MIN_DRAG = 24, FLIGHT_MS = 260, NEXT_KICK_MS = 560, ESCALATION_CAP = 24;

  // ---------- Levels: a new hazard every 3 goals (all bounds defined) ----------
  function levelFor(g){ return 1 + Math.floor(Math.min(g, ESCALATION_CAP) / 3); }
  function patrolPeriod(g){ g = Math.min(g, ESCALATION_CAP); return Math.max(780, 2100 - 65*g); }
  function diveReach(g){ g = Math.min(g, ESCALATION_CAP); return 90 + 2.5*g; }
  // How high the keeper can JUMP: glove reach climbs with streak. Standing gloves sit ~88px above the line.
  var GLOVE_BASE_Y = LINE_Y - 88;
  function jumpMax(g){ g = Math.min(g, ESCALATION_CAP); return 60 + 2.5*g; }
  function shotClock(g){ g = Math.min(g, ESCALATION_CAP); return Math.max(1500, 3200 - 80*g); }
  function windMax(g){ return g >= 6 ? Math.min(150, 60 + (g-6)*9) : 0; }
  function defenderCount(g){ return g >= 12 ? 2 : (g >= 3 ? 1 : 0); }
  function rainOn(g){ return g >= 9; }

  var UNLOCK_BANNERS = { 3:'DEFENDER ON THE PITCH!', 6:'WIND PICKING UP', 9:'RAIN — SLIPPERY AIM', 12:'TWO DEFENDERS' };
  var MILESTONES = { 5:'ON FIRE', 10:'PERFECT TEN', 15:'LEGENDARY', 20:'ICONIC' };
  var SAVE_LINES = ['Read it all day','Too easy.','Saw that coming.','Not today.','Is that all you got?'];
  var STEAL_LINES = ['Too slow!','He took it off your boot!'];
  var BLOCK_LINES = ['Threw himself at it!','Body on the line!','Denied by the last man!'];

  // ---------- State ----------
  var canvas = document.getElementById('c'), ctx = canvas.getContext('2d');
  var state = 'boot';     // boot | aiming | dragging | flight | goalpause | over
  var streak = 0, best = 0, runId = '';
  var keeper = { x: GOAL_CX, phase: 0, diveX: null, diveFromX: GOAL_CX, diveY: 0, diveT: 0 };
  var ball = { x: BALL_X, y: BALL_Y, s: 1 };
  var aim = { x: GOAL_CX, y: 230 };
  var drag = null, flight = null, flash = null, shake = 0, shakeT = 0;
  var clockLeft = shotClock(0), lastTs = 0, nextKickAt = 0, quip = null, quipT = 0;
  var trail = [], ripple = null, overLine = '', overSub = '';
  var wind = 0;
  var defenders = [];
  var rain = [];
  var banner = null, bannerT = 0;
  var slickPhase = 0;

  function newRunId(){
    try { if (crypto && crypto.randomUUID) return 'kd-' + crypto.randomUUID().slice(0, 20); } catch(e){}
    return 'kd-' + Date.now().toString(36) + '-' + Math.floor(Math.random()*1e9).toString(36);
  }

  // ---------- Bridge ----------
  function post(msg){
    var s = JSON.stringify(msg);
    if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) window.ReactNativeWebView.postMessage(s);
    else if (window.parent && window.parent !== window) window.parent.postMessage(s, '*');
  }
  window.__keeperDuelApplyStart = function(cfg){
    best = (cfg && typeof cfg.bestStreak === 'number') ? cfg.bestStreak : 0;
    restart();
  };

  // ---------- Audio (synthesized — no assets) ----------
  var actx = null;
  function ac(){ if(!actx){ try{ actx = new (window.AudioContext||window.webkitAudioContext)(); }catch(e){} }
    if (actx && actx.state==='suspended') actx.resume(); return actx; }
  function tone(type, f0, f1, t, vol){
    var c2 = ac(); if(!c2) return;
    var o = c2.createOscillator(), g = c2.createGain();
    o.type = type; o.frequency.setValueAtTime(f0, c2.currentTime);
    o.frequency.exponentialRampToValueAtTime(Math.max(1,f1), c2.currentTime + t);
    g.gain.setValueAtTime(vol, c2.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, c2.currentTime + t);
    o.connect(g); g.connect(c2.destination); o.start(); o.stop(c2.currentTime + t + 0.02);
  }
  function sfxKick(){ tone('sine', 150, 60, 0.13, 0.5); }
  function sfxGoal(){
    tone('triangle', 523, 523, 0.4, 0.15);
    setTimeout(function(){ tone('triangle', 659, 659, 0.4, 0.15); }, 70);
    setTimeout(function(){ tone('triangle', 784, 784, 0.5, 0.15); }, 140);
  }
  function sfxSave(){ tone('sawtooth', 260, 90, 0.4, 0.13); }
  function sfxTick(){ tone('square', 900, 900, 0.03, 0.05); }
  function sfxUnlock(){ tone('triangle', 392, 784, 0.35, 0.16); }
  function sfxBlock(){ tone('square', 180, 70, 0.2, 0.25); }

  // ---------- Flow ----------
  function rollWind(){
    var m = windMax(streak);
    wind = m ? (Math.random()*2 - 1) * m : 0;
    if (m && Math.abs(wind) < m*0.25) wind = m*0.35*(wind<0?-1:1); // never a dead-calm kick once wind unlocks
  }
  function spawnDefenders(){
    defenders.length = 0;
    var n = defenderCount(streak);
    for (var i = 0; i < n; i++) {
      var fromLeft = Math.random() < 0.5;
      defenders.push({
        y: 480 + i*120 + Math.random()*60,
        x: fromLeft ? -60 : W + 60,
        v: (fromLeft ? 1 : -1) * (0.22 + Math.min(streak,ESCALATION_CAP)*0.008) * (0.8 + Math.random()*0.5),
        w: 46,
      });
    }
  }
  function newKick(){
    ball.x = BALL_X; ball.y = BALL_Y; ball.s = 1;
    keeper.diveX = null; keeper.diveY = 0; keeper.diveT = 0;
    drag = null; flight = null; trail.length = 0;
    clockLeft = shotClock(streak);
    rollWind();
    spawnDefenders();
    state = 'aiming';
  }
  function endRun(title, sub, nearMiss){
    state = 'over'; overLine = title; overSub = sub;
    if (streak > best) best = streak;
    post({ type: 'duel-over', runId: runId, streak: streak, nearMiss: !!nearMiss });
  }
  function restart(){
    streak = 0; runId = newRunId();
    newKick();
    tone('sine', 1800, 2200, 0.3, 0.12); // whistle
  }
  function quipSet(s){ quip = s; quipT = performance.now(); }

  function keeperXAt(extraMs){
    var p = patrolPeriod(streak);
    var ph = keeper.phase + (extraMs / p) * Math.PI * 2;
    var amp = (GOAL_R - GOAL_L)/2 - 40;
    return GOAL_CX + Math.sin(ph) * amp;
  }

  function shoot(){
    sfxKick();
    var toX = aim.x + wind;                       // wind bends the landing point — compensate!
    var toY = aim.y;
    var inFrame = toX > GOAL_L + 10 && toX < GOAL_R - 10 && toY > BAR_Y + 10 && toY < LINE_Y - 4;
    var kx = keeperXAt(FLIGHT_MS);
    keeper.diveFromX = keeper.x;   // where the dive starts — sets the dive angle
    keeper.diveX = Math.max(GOAL_L+24, Math.min(GOAL_R-24, kx));
    // He jumps too: gloves meet the shot height, but only up to his jump ceiling — top corners beat him.
    var gloveY = Math.max(toY, GLOVE_BASE_Y - jumpMax(streak));
    keeper.diveY = Math.max(0, GLOVE_BASE_Y - gloveY);   // px of vertical leap, for the animation
    keeper.diveT = 0;
    var dist = Math.hypot(toX - keeper.diveX, toY - gloveY);
    var saved = inFrame && dist <= diveReach(streak);
    var nearMiss = inFrame && !saved && dist <= diveReach(streak) + 34;
    flight = { t0: performance.now(), fromX: ball.x, fromY: ball.y, toX: toX, toY: toY,
               outcome: !inFrame ? 'off' : (saved ? 'save' : 'goal'), nearMiss: nearMiss, blocked: false };
    state = 'flight';
  }

  function land(){
    var f = flight;
    if (f.blocked) {
      sfxBlock();
      shake = 10; shakeT = performance.now();
      flash = { t0: performance.now(), color: '#FF5C4D', label: 'BLOCKED!', ms: 800 };
      endRun('BLOCKED', BLOCK_LINES[(Math.random()*BLOCK_LINES.length)|0], false);
      return;
    }
    if (f.outcome === 'goal') {
      streak++;
      post({ type: 'goal', streak: streak });
      shake = 14; shakeT = performance.now();
      ripple = { x: f.toX, y: f.toY, t0: performance.now() };
      sfxGoal();
      var unlock = UNLOCK_BANNERS[streak];
      if (unlock) { banner = unlock; bannerT = performance.now(); sfxUnlock(); }
      var ms = MILESTONES[streak];
      flash = { t0: performance.now(), color: ms ? '#FCD34D' : '#C8FF4D',
                label: ms || ('+1  ·  ' + streak), ms: ms ? 620 : 360, big: !!ms };
      state = 'goalpause';
      nextKickAt = performance.now() + (unlock ? NEXT_KICK_MS + 700 : NEXT_KICK_MS);
    } else if (f.outcome === 'save') {
      sfxSave();
      flash = { t0: performance.now(), color: '#FF5C4D', label: f.nearMiss ? 'FINGERTIPS!' : 'SAVED', ms: 800 };
      endRun(f.nearMiss ? 'SO CLOSE' : 'SAVED', SAVE_LINES[(Math.random()*SAVE_LINES.length)|0], f.nearMiss);
    } else {
      sfxSave();
      flash = { t0: performance.now(), color: '#FF5C4D', label: 'OFF TARGET', ms: 800 };
      endRun('OFF TARGET', wind ? 'The wind took it. Watch the arrow.' : 'The frame matters.', false);
    }
  }

  // ---------- Input ----------
  function evPos(e){
    var t = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]) || e;
    var r = canvas.getBoundingClientRect();
    return { x: (t.clientX - r.left) * (W / r.width), y: (t.clientY - r.top) * (H / r.height) };
  }
  function onDown(e){
    e.preventDefault(); ac();
    if (state === 'over') { restart(); return; }
    if (state !== 'aiming') return;
    var p = evPos(e);
    if (p.y < H * 0.3) return;
    drag = { sx: p.x, sy: p.y };
    aim.x = GOAL_CX; aim.y = 230;
    state = 'dragging';
  }
  function onMove(e){
    e.preventDefault();
    if (state !== 'dragging' || !drag) return;
    var p = evPos(e);
    aim.x = Math.max(GOAL_L - 60, Math.min(GOAL_R + 60, GOAL_CX + (p.x - drag.sx) * 2.6));
    aim.y = Math.max(BAR_Y - 26, Math.min(LINE_Y - 8, 235 + (p.y - drag.sy) * 1.8));
  }
  function onUp(e){
    e.preventDefault();
    if (state !== 'dragging' || !drag) return;
    var p = evPos(e);
    var moved = Math.hypot(p.x - drag.sx, p.y - drag.sy);
    drag = null;
    if (moved < MIN_DRAG) { state = 'aiming'; return; }
    shoot();
  }
  ['touchstart','mousedown'].forEach(function(n){ canvas.addEventListener(n, onDown, { passive:false }); });
  ['touchmove','mousemove'].forEach(function(n){ canvas.addEventListener(n, onMove, { passive:false }); });
  ['touchend','mouseup','touchcancel'].forEach(function(n){ canvas.addEventListener(n, onUp, { passive:false }); });

  // ---------- Update ----------
  function update(ts){
    var dt = lastTs ? Math.min(50, ts - lastTs) : 16; // clamp so a backgrounded tab can't drain the clock
    lastTs = ts;
    slickPhase += dt;

    if (keeper.diveX === null) {
      keeper.phase += (dt / patrolPeriod(streak)) * Math.PI * 2;
      var amp = (GOAL_R - GOAL_L)/2 - 40;
      keeper.x = GOAL_CX + Math.sin(keeper.phase) * amp;
    } else {
      keeper.diveT = Math.min(1, keeper.diveT + dt / 180);
      keeper.x += (keeper.diveX - keeper.x) * keeper.diveT;
    }

    // Rain slick: the crosshair drifts while dragging — you fight to hold it steady.
    if (rainOn(streak) && state === 'dragging') {
      aim.x += Math.sin(slickPhase/140) * 1.7 + Math.sin(slickPhase/61) * 0.9;
    }

    // Defenders sprint across; wrap around so the threat is constant while you aim.
    for (var i = 0; i < defenders.length; i++) {
      var d = defenders[i];
      d.x += d.v * dt;
      if (d.v > 0 && d.x > W + 70) d.x = -70;
      if (d.v < 0 && d.x < -70) d.x = W + 70;
    }

    if (state === 'aiming' || state === 'dragging') {
      var prev = clockLeft;
      clockLeft -= dt;
      if (clockLeft <= 1000 && Math.floor(prev/250) !== Math.floor(clockLeft/250)) sfxTick();
      if (clockLeft <= 0) {
        flash = { t0: performance.now(), color: '#FF5C4D', label: 'STOLEN!', ms: 800 };
        sfxSave();
        endRun('HE STOLE IT', STEAL_LINES[(Math.random()*STEAL_LINES.length)|0], false);
      }
    }

    if (state === 'flight' && flight) {
      var k = Math.min(1, (ts - flight.t0) / FLIGHT_MS);
      var e2 = 1 - (1-k)*(1-k);
      // Wind bends the path progressively (quadratic drift toward the wind-adjusted landing point).
      var straightX = flight.fromX + (flight.toX - wind - flight.fromX) * e2;
      ball.x = straightX + wind * e2 * e2;
      ball.y = flight.fromY + (flight.toY - flight.fromY) * e2;
      ball.s = 1 - 0.4 * e2;
      trail.push({ x: ball.x, y: ball.y, t: ts });
      if (!flight.blocked) {
        for (var j = 0; j < defenders.length; j++) {
          var dd = defenders[j];
          if (Math.abs(ball.y - (dd.y - 40)) < 46 && Math.abs(ball.x - dd.x) < dd.w) {
            flight.blocked = true;
            flight.t0 = ts - FLIGHT_MS; // land immediately
            break;
          }
        }
      }
      if (k >= 1) land();
    }
    while (trail.length && ts - trail[0].t > 260) trail.shift();

    if (state === 'goalpause' && ts >= nextKickAt) newKick();

    if (rainOn(streak)) {
      if (rain.length < 90) rain.push({ x: Math.random()*W, y: -10, v: 0.55 + Math.random()*0.3 });
      for (var r2 = rain.length - 1; r2 >= 0; r2--) {
        var dr = rain[r2];
        dr.y += dr.v * dt; dr.x += dt * 0.08;
        if (dr.y > H) rain.splice(r2, 1);
      }
    } else rain.length = 0;
  }

  // ---------- Render ----------
  function draw(ts){
    ctx.save();
    if (shake > 0) {
      var sk = Math.max(0, 1 - (ts - shakeT)/160) * shake;
      if (sk <= 0.3) shake = 0;
      ctx.translate((Math.random()-0.5)*sk, (Math.random()-0.5)*sk);
    }
    ctx.clearRect(-20, -20, W+40, H+40);

    var lvl = levelFor(streak);
    var sky = ctx.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, '#0B1220'); sky.addColorStop(0.42, rainOn(streak) ? '#0E2436' : '#12304A');
    sky.addColorStop(0.45, '#1E5128'); sky.addColorStop(1, '#153A1D');
    ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
    ctx.globalAlpha = 0.07; ctx.fillStyle = '#FFF';
    for (var i = 0; i < 6; i++) ctx.fillRect(0, 470 + i*100, W, 46);
    ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(255,255,255,.5)';
    ctx.beginPath(); ctx.ellipse(BALL_X, BALL_Y + 34, 30, 8, 0, 0, Math.PI*2); ctx.fill();

    // Net with goal ripple
    ctx.strokeStyle = 'rgba(244,251,246,.18)'; ctx.lineWidth = 1;
    var rip = null;
    if (ripple) { var rk = (ts - ripple.t0)/380; if (rk >= 1) ripple = null; else rip = { x: ripple.x, y: ripple.y, k: rk }; }
    for (var x = GOAL_L; x <= GOAL_R; x += 24) {
      ctx.beginPath();
      for (var y = BAR_Y; y <= LINE_Y; y += 8) {
        var ox = 0;
        if (rip) { var d3 = Math.hypot(x-rip.x, y-rip.y); ox = Math.sin(d3/18 - rip.k*14) * 6 * Math.max(0,1-rip.k) * Math.max(0, 1 - d3/220); }
        if (y === BAR_Y) ctx.moveTo(x+ox, y); else ctx.lineTo(x+ox, y);
      }
      ctx.stroke();
    }
    for (var y2 = BAR_Y; y2 <= LINE_Y; y2 += 24) { ctx.beginPath(); ctx.moveTo(GOAL_L, y2); ctx.lineTo(GOAL_R, y2); ctx.stroke(); }
    ctx.strokeStyle = '#F4FBF6'; ctx.lineWidth = 10; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(GOAL_L, LINE_Y); ctx.lineTo(GOAL_L, BAR_Y); ctx.lineTo(GOAL_R, BAR_Y); ctx.lineTo(GOAL_R, LINE_Y); ctx.stroke();

    drawKeeper();
    drawDefenders();

    // Aim marker
    if (state === 'dragging') {
      var inF = aim.x > GOAL_L && aim.x < GOAL_R && aim.y > BAR_Y;
      ctx.strokeStyle = inF ? '#C8FF4D' : '#FF5C4D'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(aim.x, aim.y, 22, 0, Math.PI*2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(aim.x-32, aim.y); ctx.lineTo(aim.x+32, aim.y);
      ctx.moveTo(aim.x, aim.y-32); ctx.lineTo(aim.x, aim.y+32); ctx.stroke();
    }

    // Trail + ball
    for (var t2 = 0; t2 < trail.length; t2++) {
      var tr = trail[t2], a = (ts - tr.t)/260;
      ctx.globalAlpha = Math.max(0, 0.35*(1-a));
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath(); ctx.arc(tr.x, tr.y, BALL_R*0.55*(1-a*0.5), 0, Math.PI*2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    var bg = ctx.createRadialGradient(ball.x-6*ball.s, ball.y-7*ball.s, 2, ball.x, ball.y, BALL_R*ball.s);
    bg.addColorStop(0, '#FFF'); bg.addColorStop(0.75, '#DDE1E6'); bg.addColorStop(1, '#9CA3AF');
    ctx.fillStyle = bg; ctx.beginPath(); ctx.arc(ball.x, ball.y, BALL_R*ball.s, 0, Math.PI*2); ctx.fill();
    ctx.fillStyle = '#1F2937'; ctx.beginPath(); ctx.arc(ball.x, ball.y, 6*ball.s, 0, Math.PI*2); ctx.fill();

    // Rain overlay
    if (rain.length) {
      ctx.strokeStyle = 'rgba(160,200,255,.5)'; ctx.lineWidth = 1.6;
      ctx.beginPath();
      for (var r3 = 0; r3 < rain.length; r3++) {
        var rr = rain[r3];
        ctx.moveTo(rr.x, rr.y); ctx.lineTo(rr.x - 3, rr.y + 16);
      }
      ctx.stroke();
    }

    drawHud(ts, lvl);
    drawFlash(ts);
    drawBanner(ts);
    if (state === 'over') drawOver();
    ctx.restore();
  }

  function drawKeeper(){
    ctx.save();
    var leap = keeper.diveX !== null ? keeper.diveY * Math.sin(Math.min(1, keeper.diveT) * Math.PI * 0.5) : 0;
    ctx.translate(keeper.x, LINE_Y - leap);
    if (keeper.diveX !== null) {
      // Dive angle: rotate toward the ball — flat sprawl for far low dives, steep for vertical leaps.
      var dvx = keeper.diveX - keeper.diveFromX;
      var flatness = Math.min(1, Math.abs(dvx) / 170);
      var vertical = Math.min(1, keeper.diveY / 100);
      var maxLean = (0.45 + 1.0 * flatness) * (1 - 0.55 * vertical);
      var lean = (dvx >= 0 ? 1 : -1) * maxLean * Math.sin(Math.min(1, keeper.diveT) * Math.PI * 0.5);
      ctx.rotate(lean);
    }
    var spread = keeper.diveX !== null ? 44 : 26 + Math.sin(performance.now()/300)*4;
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(-13, -34, 10, 34); ctx.fillRect(3, -34, 10, 34);
    var kit = ctx.createLinearGradient(0, -92, 0, -30);
    kit.addColorStop(0, '#FBBF24'); kit.addColorStop(1, '#D97706');
    ctx.fillStyle = kit;
    ctx.fillRect(-17, -92, 34, 62);
    ctx.fillRect(-spread-9, -84, 12, 42);
    ctx.fillRect(spread-3, -84, 12, 42);
    ctx.fillStyle = '#E5E7EB';
    ctx.beginPath(); ctx.arc(-spread-3, -88, 9, 0, Math.PI*2); ctx.fill();
    ctx.beginPath(); ctx.arc(spread+3, -88, 9, 0, Math.PI*2); ctx.fill();
    ctx.fillStyle = '#F3C89D';
    ctx.beginPath(); ctx.arc(0, -104, 14, 0, Math.PI*2); ctx.fill();
    ctx.restore();
  }

  function drawDefenders(){
    for (var i = 0; i < defenders.length; i++) {
      var d = defenders[i];
      ctx.save();
      ctx.translate(d.x, d.y);
      var run = Math.sin(performance.now()/90 + i*2) * 8;
      ctx.fillStyle = '#0F172A';
      ctx.fillRect(-10 + run*0.4, -30, 8, 30);
      ctx.fillRect(4 - run*0.4, -30, 8, 30);
      ctx.fillStyle = '#DC2626';
      ctx.fillRect(-14, -74, 28, 46);
      ctx.fillStyle = '#F3C89D';
      ctx.beginPath(); ctx.arc(0, -86, 12, 0, Math.PI*2); ctx.fill();
      ctx.restore();
    }
  }

  function drawHud(ts, lvl){
    ctx.textAlign = 'center';
    var pop = flash && (ts - flash.t0) < 220 ? 1 + 0.35*(1-(ts-flash.t0)/220) : 1;
    ctx.save();
    ctx.translate(W/2, 84); ctx.scale(pop, pop);
    ctx.fillStyle = '#F4FBF6'; ctx.font = '800 68px -apple-system,system-ui,sans-serif';
    ctx.fillText(String(streak), 0, 0);
    ctx.restore();
    ctx.fillStyle = 'rgba(244,251,246,.55)'; ctx.font = '600 22px -apple-system,system-ui,sans-serif';
    ctx.fillText('BEST ' + Math.max(best, streak) + '   ·   LVL ' + lvl, W/2, 118);

    if (state === 'aiming' || state === 'dragging') {
      var frac = Math.max(0, clockLeft / shotClock(streak));
      var bw = 300;
      ctx.fillStyle = 'rgba(255,255,255,.14)';
      ctx.fillRect(W/2 - bw/2, 388, bw, 10);
      ctx.fillStyle = frac > 0.4 ? '#C8FF4D' : '#FF5C4D';
      ctx.fillRect(W/2 - bw/2, 388, bw*frac, 10);
    }

    if (wind && state !== 'over' && state !== 'boot') {
      var wx = W/2, wy = 435, len = Math.min(90, Math.abs(wind) * 0.55) * (wind > 0 ? 1 : -1);
      ctx.strokeStyle = '#7DD3FC'; ctx.lineWidth = 5; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(wx - len, wy); ctx.lineTo(wx + len, wy); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(wx + len + (wind>0?10:-10), wy);
      ctx.lineTo(wx + len - (wind>0?8:-8), wy - 8);
      ctx.lineTo(wx + len - (wind>0?8:-8), wy + 8);
      ctx.closePath();
      ctx.fillStyle = '#7DD3FC'; ctx.fill();
      ctx.fillStyle = 'rgba(125,211,252,.85)'; ctx.font = '700 20px -apple-system,system-ui,sans-serif';
      ctx.fillText('WIND', wx, wy + 30);
    }

    if (quip && performance.now() - quipT < 1400) {
      ctx.fillStyle = '#FCD34D'; ctx.font = '700 26px -apple-system,system-ui,sans-serif';
      ctx.fillText(quip, W/2, 470);
    }
    if (state === 'aiming' && streak === 0) {
      ctx.fillStyle = 'rgba(244,251,246,.62)'; ctx.font = '600 24px -apple-system,system-ui,sans-serif';
      ctx.fillText('Flick past him — before he steals it', W/2, H-90);
    }
  }

  function drawFlash(ts){
    if (!flash) return;
    var k = (ts - flash.t0)/flash.ms;
    if (k >= 1) { if (state !== 'over') flash = null; return; }
    var a = k < 0.25 ? k/0.25 : 1-(k-0.25)/0.75;
    ctx.globalAlpha = a * (flash.big ? 0.38 : 0.22);
    ctx.fillStyle = flash.color;
    ctx.fillRect(0, 0, W, H);
    ctx.globalAlpha = Math.min(1, a*1.5);
    ctx.fillStyle = flash.color;
    ctx.font = (flash.big ? '800 78px' : '800 56px') + ' -apple-system,system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(flash.label, W/2, H/2 - 60);
    ctx.globalAlpha = 1;
  }

  function drawBanner(ts){
    if (!banner) return;
    var k = (ts - bannerT) / 1900;
    if (k >= 1) { banner = null; return; }
    var a = k < 0.15 ? k/0.15 : (k > 0.8 ? (1-k)/0.2 : 1);
    ctx.globalAlpha = a;
    ctx.fillStyle = 'rgba(11,18,32,.85)';
    ctx.fillRect(0, 560, W, 86);
    ctx.fillStyle = '#7DD3FC';
    ctx.font = '800 38px -apple-system,system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(banner, W/2, 616);
    ctx.globalAlpha = 1;
  }

  function drawOver(){
    ctx.fillStyle = 'rgba(11,18,32,.72)';
    ctx.fillRect(0, H/2 - 10, W, 260);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#F4FBF6'; ctx.font = '800 54px -apple-system,system-ui,sans-serif';
    ctx.fillText(overLine, W/2, H/2 + 66);
    ctx.fillStyle = '#FCD34D'; ctx.font = '600 26px -apple-system,system-ui,sans-serif';
    ctx.fillText(overSub, W/2, H/2 + 110);
    ctx.fillStyle = 'rgba(244,251,246,.7)'; ctx.font = '700 28px -apple-system,system-ui,sans-serif';
    ctx.fillText(streak + ' goals  ·  best ' + Math.max(best, streak), W/2, H/2 + 170);
  }

  function frame(ts){ update(ts); draw(ts); requestAnimationFrame(frame); }
  requestAnimationFrame(frame);
  post({ type: 'ready' });
})();
</script>
</body>
</html>`;
}
